import { useMemo, useState } from "react";
import { Ruler, Scissors, Layers } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { formatEUR } from "@/lib/api";
import { useMaterials } from "@/lib/materials";
import OptimizatorRazreza from "@/components/OptimizatorRazreza";

export const MATERIALS = [
  { id: "iveral18", name: "Oplemeniteni iveral 18 mm", priceM2: 32.9 },
  { id: "iveral25", name: "Oplemeniteni iveral 25 mm", priceM2: 43.5 },
  { id: "mdf8", name: "MDF surovi 8 mm", priceM2: 12.9 },
  { id: "mdf18", name: "MDF surovi 18 mm", priceM2: 19.9 },
  { id: "vezana18", name: "Vezana plošča breza 18 mm", priceM2: 48.9 },
  { id: "osb18", name: "OSB-3 18 mm", priceM2: 10.5 },
];

export const EDGE_TYPES = [
  { id: "abs1", name: "ABS trak 1 mm", priceM: 2.4 },
  { id: "abs2", name: "ABS trak 2 mm", priceM: 3.6 },
];

export const CUT_PRICE_M = 1.2;

export default function RazrezConfigurator() {
  const { addItem } = useCart();
  const materials = useMaterials();
  const [materialId, setMaterialId] = useState("iveral18");
  const [length, setLength] = useState(1200);
  const [width, setWidth] = useState(600);
  const [lengthText, setLengthText] = useState("1200");
  const [widthText, setWidthText] = useState("600");

  const commitLength = () => {
    const v = Math.max(100, Math.min(2800, parseInt(lengthText, 10) || 100));
    setLength(v);
    setLengthText(String(v));
  };
  const commitWidth = () => {
    const v = Math.max(100, Math.min(2070, parseInt(widthText, 10) || 100));
    setWidth(v);
    setWidthText(String(v));
  };
  const [qty, setQty] = useState(1);
  const [edgeTypeId, setEdgeTypeId] = useState("abs1");
  const [edges, setEdges] = useState({ A: false, B: false, C: false, D: false });

  const calc = useMemo(() => {
    const material = MATERIALS.find((m) => m.id === materialId);
    const edgeType = EDGE_TYPES.find((e) => e.id === edgeTypeId);
    const areaM2 = (length * width) / 1e6;
    const perimeterM = (2 * (length + width)) / 1000;
    const edgeM =
      ((edges.A ? length : 0) + (edges.C ? length : 0) +
        (edges.B ? width : 0) + (edges.D ? width : 0)) / 1000;
    const materialCost = areaM2 * material.priceM2 * qty;
    const cutCost = perimeterM * CUT_PRICE_M * qty;
    const edgeCost = edgeM * edgeType.priceM * qty;
    return {
      material, edgeType, areaM2, perimeterM, edgeM,
      materialCost, cutCost, edgeCost,
      total: materialCost + cutCost + edgeCost,
    };
  }, [materialId, length, width, qty, edges, edgeTypeId]);

  const toggleEdge = (k) => setEdges((p) => ({ ...p, [k]: !p[k] }));

  const handleAdd = () => {
    const unitPrice = calc.total / qty;
    addItem({
      key: `custom-${Date.now()}`,
      name: `RAZREZ PO MERI – ${calc.material.name}`,
      price: Math.round(unitPrice * 100) / 100,
      qty,
      unit: "kos",
      image: "https://images.unsplash.com/photo-1560846389-956694677531?auto=format&fit=crop&w=400&q=80",
      meta: `${length} × ${width} mm${calc.edgeM > 0 ? ` · robljenje ${calc.edgeType.name} (${calc.edgeM.toFixed(2)} m)` : ""}`,
    });
    toast.success("Razrez dodan v košarico", {
      description: `${length} × ${width} mm, ${qty} kos — ${formatEUR(calc.total)}`,
    });
  };

  const previewRatio = Math.min(Math.max(length / Math.max(width, 1), 0.3), 3);

  return (
    <section id="razrez" className="py-20 lg:py-28 bg-[#1A1D1A] relative overflow-hidden" data-testid="razrez-section">
      <div className="absolute inset-0 texture-grain opacity-[0.06] pointer-events-none" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="max-w-2xl mb-12">
          <p className="font-tech text-xs uppercase tracking-[0.2em] text-[#D97706] mb-3">Spletni razrez</p>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#FBF9F5] leading-snug">
            Razrez in robljenje po meri
          </h2>
          <p className="mt-4 text-stone-400 leading-relaxed">
            Vnesite dimenzije, izberite material in robni trak — ceno izračunamo takoj. Natančnost reza ±0,1 mm na CNC formatni žagi.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-7 bg-[#232723] border border-[#323832] rounded-2xl p-6 sm:p-8 space-y-6">
            <div>
              <label className="flex items-center gap-2 text-sm font-semibold text-[#FBF9F5] mb-2">
                <Layers className="w-4 h-4 text-[#D97706]" /> Vrsta plošče
              </label>
              <select
                data-testid="cutting-material-select"
                value={materialId}
                onChange={(e) => setMaterialId(e.target.value)}
                className="w-full h-11 px-4 rounded-lg bg-[#1A1D1A] border border-[#323832] text-[#FBF9F5] text-sm focus:outline-none focus:ring-2 focus:ring-[#D97706]/50"
              >
                {materials.map((m) => (
                  <option key={m.id} value={m.id}>{m.name} — {formatEUR(m.priceM2)}/m²</option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="text-xs font-tech uppercase tracking-wider text-stone-400 block mb-2">Dolžina (mm)</label>
                <input
                  data-testid="cutting-length-input"
                  type="number" min="100" max="2800" value={lengthText}
                  onChange={(e) => setLengthText(e.target.value)}
                  onBlur={commitLength}
                  onKeyDown={(e) => { if (e.key === "Enter") { commitLength(); e.target.blur(); } }}
                  className="w-full h-11 px-4 rounded-lg bg-[#1A1D1A] border border-[#323832] text-[#FBF9F5] text-sm font-tech focus:outline-none focus:ring-2 focus:ring-[#D97706]/50"
                />
                <input
                  data-testid="cutting-length-slider"
                  type="range" min="100" max="2800" step="10" value={length}
                  onChange={(e) => { setLength(Number(e.target.value)); setLengthText(e.target.value); }}
                  className="w-full mt-2 accent-[#D97706]"
                />
              </div>
              <div>
                <label className="text-xs font-tech uppercase tracking-wider text-stone-400 block mb-2">Širina (mm)</label>
                <input
                  data-testid="cutting-width-input"
                  type="number" min="100" max="2070" value={widthText}
                  onChange={(e) => setWidthText(e.target.value)}
                  onBlur={commitWidth}
                  onKeyDown={(e) => { if (e.key === "Enter") { commitWidth(); e.target.blur(); } }}
                  className="w-full h-11 px-4 rounded-lg bg-[#1A1D1A] border border-[#323832] text-[#FBF9F5] text-sm font-tech focus:outline-none focus:ring-2 focus:ring-[#D97706]/50"
                />
                <input
                  data-testid="cutting-width-slider"
                  type="range" min="100" max="2070" step="10" value={width}
                  onChange={(e) => { setWidth(Number(e.target.value)); setWidthText(e.target.value); }}
                  className="w-full mt-2 accent-[#D97706]"
                />
              </div>
              <div>
                <label className="text-xs font-tech uppercase tracking-wider text-stone-400 block mb-2">Količina (kos)</label>
                <input
                  data-testid="cutting-qty-input"
                  type="number" min="1" max="200" value={qty}
                  onChange={(e) => setQty(Math.max(1, Math.min(200, Number(e.target.value) || 1)))}
                  className="w-full h-11 px-4 rounded-lg bg-[#1A1D1A] border border-[#323832] text-[#FBF9F5] text-sm font-tech focus:outline-none focus:ring-2 focus:ring-[#D97706]/50"
                />
              </div>
            </div>

            <div>
              <label className="flex items-center gap-2 text-sm font-semibold text-[#FBF9F5] mb-3">
                <Scissors className="w-4 h-4 text-[#D97706]" /> Robljenje stranic
              </label>
              <div className="flex flex-wrap gap-2">
                <button
                  data-testid="edge-toggle-all"
                  onClick={() => {
                    const allOn = edges.A && edges.B && edges.C && edges.D;
                    setEdges({ A: !allOn, B: !allOn, C: !allOn, D: !allOn });
                  }}
                  className={`px-4 h-10 rounded-full text-sm font-semibold border transition-colors ${
                    edges.A && edges.B && edges.C && edges.D
                      ? "bg-[#D97706] border-[#D97706] text-white"
                      : "bg-transparent border-[#D97706]/60 text-[#D97706] hover:bg-[#D97706]/10"
                  }`}
                >
                  Vse stranice
                </button>
                {[
                  { k: "A", label: "A · zgornja" },
                  { k: "B", label: "B · desna" },
                  { k: "C", label: "C · spodnja" },
                  { k: "D", label: "D · leva" },
                ].map(({ k, label }) => (
                  <button
                    key={k}
                    data-testid={`edge-toggle-${k.toLowerCase()}`}
                    onClick={() => toggleEdge(k)}
                    className={`px-4 h-10 rounded-full text-sm font-semibold border transition-colors ${
                      edges[k]
                        ? "bg-[#D97706] border-[#D97706] text-white"
                        : "bg-transparent border-[#323832] text-stone-400 hover:border-[#D97706]/60"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-xs font-tech uppercase tracking-wider text-stone-400 block mb-2">Vrsta robnega traku</label>
              <div className="flex gap-2">
                {EDGE_TYPES.map((e) => (
                  <button
                    key={e.id}
                    data-testid={`edge-type-${e.id}`}
                    onClick={() => setEdgeTypeId(e.id)}
                    className={`flex-1 px-4 h-11 rounded-lg text-sm font-semibold border transition-colors ${
                      edgeTypeId === e.id
                        ? "bg-[#FBF9F5] border-[#FBF9F5] text-[#1A1D1A]"
                        : "bg-transparent border-[#323832] text-stone-400 hover:border-[#FBF9F5]/40"
                    }`}
                  >
                    {e.name} · {formatEUR(e.priceM)}/m
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="lg:col-span-5 space-y-6">
            <div className="bg-[#232723] border border-[#323832] rounded-2xl p-6 sm:p-8 flex flex-col items-center">
              <p className="text-xs font-tech uppercase tracking-wider text-stone-400 mb-5 self-start">
                <Ruler className="w-3.5 h-3.5 inline mr-1 text-[#D97706]" /> Predogled kosa
              </p>
              <div
                className="relative bg-[#B45309]/25 border-2 border-[#B45309] rounded-sm transition-all duration-300"
                style={{ width: "220px", height: `${Math.round(220 / previewRatio)}px`, maxHeight: "300px" }}
                data-testid="cutting-preview"
              >
                {edges.A && <div className="absolute top-0 left-0 right-0 h-1.5 bg-[#D97706]" />}
                {edges.B && <div className="absolute top-0 bottom-0 right-0 w-1.5 bg-[#D97706]" />}
                {edges.C && <div className="absolute bottom-0 left-0 right-0 h-1.5 bg-[#D97706]" />}
                {edges.D && <div className="absolute top-0 bottom-0 left-0 w-1.5 bg-[#D97706]" />}
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="font-tech text-xs text-[#FBF9F5] bg-[#1A1D1A]/80 px-2 py-1 rounded">
                    {length} × {width} mm
                  </span>
                </div>
              </div>
              <p className="mt-4 text-xs text-stone-500">Oranžna črta = obrobljeni rob ({calc.edgeType.name})</p>
            </div>

            <div className="bg-[#FBF9F5] rounded-2xl p-6 sm:p-8" data-testid="cutting-summary">
              <h3 className="font-display font-bold text-lg text-[#1A1D1A] mb-4">Izračun cene</h3>
              <dl className="space-y-2.5 text-sm">
                <div className="flex justify-between">
                  <dt className="text-stone-600">Material ({calc.areaM2.toFixed(2)} m² × {qty} kos)</dt>
                  <dd className="font-tech font-semibold" data-testid="summary-material-cost">{formatEUR(calc.materialCost)}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-stone-600">Razrez ({calc.perimeterM.toFixed(2)} m × {qty})</dt>
                  <dd className="font-tech font-semibold" data-testid="summary-cut-cost">{formatEUR(calc.cutCost)}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-stone-600">Robljenje ({calc.edgeM.toFixed(2)} m × {qty})</dt>
                  <dd className="font-tech font-semibold" data-testid="summary-edge-cost">{formatEUR(calc.edgeCost)}</dd>
                </div>
                <div className="flex justify-between pt-3 border-t border-[#E4E0D7] font-display font-bold text-lg">
                  <dt>Skupaj z DDV</dt>
                  <dd className="font-tech text-[#B45309]" data-testid="summary-total">{formatEUR(calc.total)}</dd>
                </div>
              </dl>
              <button
                data-testid="cutting-add-to-cart-btn"
                onClick={handleAdd}
                className="mt-6 w-full h-12 rounded-full bg-[#2D4030] text-[#FBF9F5] font-semibold hover:bg-[#1E2C20] transition-colors"
              >
                Dodaj razrez v košarico
              </button>
            </div>
          </div>
        </div>

        <OptimizatorRazreza />
      </div>
    </section>
  );
}
